import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';

interface DrawerContextType {
  open: boolean;
  onClose: () => void;
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export function useDrawerContext() {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('Using useDrawerContext outside DrawerContextProvider is not allowed.');
  }
  return context;
}

interface UseDrawerContextProviderVM {
  /** @default false */
  open?: DrawerContextType['open'];
  /** @default () => {} */
  onClose?: DrawerContextType['onClose'];
}

function useDrawerContextProviderVM({ open: _open = false, onClose: _onClose = () => {} }: UseDrawerContextProviderVM) {
  const [open, setOpen] = useState<boolean>(_open);
  const onClose = useRef<() => void>(_onClose);

  useEffect(
    function setInitialValues() {
      if (_open != null) setOpen(_open);
      if (_onClose != null) onClose.current = _onClose;
    },
    [_open, _onClose]
  );

  return useMemo(
    () => ({
      open,
      onClose: onClose.current,
    }),
    [open]
  );
}

export interface DrawerContextProviderProps extends UseDrawerContextProviderVM {
  children: ReactNode;
}

export function DrawerContextProvider({ children, ...vmProps }: DrawerContextProviderProps) {
  const providerValue = useDrawerContextProviderVM(vmProps);
  return <DrawerContext.Provider value={providerValue}>{children}</DrawerContext.Provider>;
}
